<!--
 * SvgIcon loads svg file as component. 
 * https://github.com/jpkleemans/vite-svg-loader
 *
 * 
 * Load by name (assets/images/icons/${this.kind}/${this.name.slice(-2)}px/${this.name}.svg?component`)
 *
 * @example icon file under path src/assets/images/icons/32px/Note32.svg
 * ..
 * <svg-icon name="Note32" @click="..." />
 *
-->
<template>
  <component
    :is="iconComponent"
    :class="[attrs.class, iconColourClass]"
    class="sls-icon"
    :name="name"
    :kind="validatedKind"
    v-bind="attrs"
    :viewBox="viewBox"
  >
  </component>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, markRaw, onMounted, useAttrs } from "vue";
import { ref, watch } from "vue";

defineOptions({
  inheritAttrs: false,
});

// Props
interface Props {
  name: string;
  kind: string;
  viewBox?: string;
  preserveColors?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  viewBox: undefined,
  preserveColors: undefined,
});

// Composables
const attrs = useAttrs();

// Data
const iconComponent = ref(null);

// Computed
const validatedKind = computed(() => {
  return validateKind(props.kind);
});

const preservedColorFamilies = ["Game", "Salis"] as const;
const aiIconFamilies = [
  "Salis",
  "DataAssistant",
  "LearningAssistant",
  "LearningOutcomeAssistant",
  "LearningProgress",
  "AdaptiveLearning",
  "AbilitySnapshot",
  "ACP",
] as const;

const matchesIconFamily = (families: readonly string[]): boolean => {
  return families.some((family) => props.name.includes(family));
};

const shouldPreserveColors = computed(() => {
  return props.preserveColors ?? matchesIconFamily(preservedColorFamilies);
});

const iconColourClass = computed(() => ({
  "icon-preserve-colors": shouldPreserveColors.value,
  "icon-themed": !shouldPreserveColors.value,
  "icon-ai": matchesIconFamily(aiIconFamilies),
}));

// Methods
const getSvgIcon = () => {
  if (!props.name) return null;

  const size = props.name.slice(-2);
  const component = defineAsyncComponent(
    () =>
      import(
        `../../../assets/images/icons/${validatedKind.value}/${size}px/${props.name}.svg?component`
      ),
  );
  return markRaw(component);
};

const validateKind = (value: string): string => {
  const validKinds = [
    "action",
    "forms",
    "indicative",
    "illustration",
    "misc",
    "richtext",
    "spaces",
    "others",
  ];
  return validKinds.includes(value) ? value : "action";
};

// Watch
watch(
  () => props.name,
  () => {
    iconComponent.value = getSvgIcon();
  },
);

// Lifecycle hook
onMounted(() => {
  iconComponent.value = getSvgIcon();
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";

.sls-icon {
  &.icon-ai-colored {
    [stroke] {
      stroke: $ai;
    }

    [fill] {
      fill: $ai;
      &[fill="#DFE8F9"] {
        opacity: 0.15;
      }
    }
  }

  // Set icon mask layer as #fff important to prevent color overwrite
  mask {
    > [fill="#fff"] {
      fill: $icon-03 !important;
    }
  }

  g {
    clip-path: none;
  }
}
</style>
